import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Users, Search, Upload, Download, Edit2, Trash2, User, Building2, Phone, Mail, Calendar, CreditCard, UserPlus, Shield, Clock, MapPin } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = [
  { value: 'Production', label: 'Production' },
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'HR & Admin', label: 'HR & Admin' },
  { value: 'MDO', label: 'MDO' },
  { value: 'Inventory', label: 'Inventory' },
  { value: 'Accounts', label: 'Accounts' },
  { value: 'Design', label: 'Design' },
  { value: 'Management', label: 'Management' },
  { value: 'Security', label: 'Security' }
];

const EMPLOYMENT_TYPES = [
  { value: 'Full-time', label: 'Full-time' },
  { value: 'Part-time', label: 'Part-time' },
  { value: 'Contract', label: 'Contract' },
  { value: 'Intern', label: 'Intern' }
];

const STAFF_TYPES = [
  { value: 'Office Staff', label: 'Office Staff' },
  { value: 'Shopfloor Staff', label: 'Shopfloor Staff' }
];

const GENDERS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' }
];

const statusColors = {
  'Active': 'bg-green-100 text-green-800',
  'Inactive': 'bg-gray-100 text-gray-800',
  'Resigned': 'bg-red-100 text-red-800',
  'Terminated': 'bg-red-100 text-red-800'
};

const staffTypeColors = {
  'Office Staff': 'bg-blue-100 text-blue-800',
  'Shopfloor Staff': 'bg-orange-100 text-orange-800'
};

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  gender: '',
  current_address: '',
  permanent_address: '',
  date_of_joining: '',
  department: '',
  designation: '',
  designation_id: '',  // Link to Designation Master
  reporting_manager_id: '',
  employment_type: 'Full-time',
  status: 'Active',
  staff_type: 'Office Staff',
  working_hours: { shift_start: '09:00', shift_end: '18:00' },
  aadhar_number: '',
  pan_number: '',
  salary_info: { basic: 0, hra: 0, allowances: 0, deductions: 0 },
  bank_details: { account_number: '', ifsc_code: '', bank_name: '', branch: '' },
  emergency_contact: { name: '', phone: '', relation: '' },
  create_user_account: false,
  user_password: '',
  user_role: 'employee'
};

export default function EmployeeMasterPage() {
  const { user, getAuthHeader } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [designations, setDesignations] = useState([]);  // Designations from master
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('Active');
  const [filterStaffType, setFilterStaffType] = useState('all');
  const [open, setOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [activeTab, setActiveTab] = useState('basic');
  const [uploading, setUploading] = useState(false);
  
  // Create System User state
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [selectedEmpForUser, setSelectedEmpForUser] = useState(null);
  const [userPassword, setUserPassword] = useState('');
  const [userRole, setUserRole] = useState('employee');
  const [creatingUser, setCreatingUser] = useState(false);
  const [restrictedAccess, setRestrictedAccess] = useState(false);
  const [selectedModules, setSelectedModules] = useState(['my_tasks']);
  
  // Available modules for restricted access
  const AVAILABLE_MODULES = [
    { value: 'my_tasks', label: 'My Tasks', description: 'View & complete assigned tasks' },
    { value: 'delegation', label: 'Delegation Tasks', description: 'View delegation tasks' },
    { value: 'checklists', label: 'Checklists', description: 'View recurring checklists' },
    { value: 'fms', label: 'FMS Workflows', description: 'View FMS workflow steps' },
  ];

  const fetchEmployees = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterDepartment && filterDepartment !== 'all') params.append('department', filterDepartment);
      
      const response = await axios.get(`${API_URL}/hr-employees?${params}`, { headers: getAuthHeader() });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to fetch employees');
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await axios.get(`${API_URL}/designations?active_only=true`, { headers: getAuthHeader() });
      setDesignations(response.data);
    } catch (error) {
      console.error('Failed to fetch designations');
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchDesignations();
  }, [filterDepartment, filterStatus, filterStaffType]);

  // When designation_id changes, auto-fill department from designation
  const handleDesignationChange = (designationId) => {
    const designation = designations.find(d => d.id === designationId);
    if (designation) {
      setFormData(prev => ({
        ...prev,
        designation_id: designationId,
        designation: designation.name,
        department: designation.department || prev.department
      }));
    }
  };

  const filteredEmployees = employees.filter(emp => {
    // Filter by staff type first
    if (filterStaffType && filterStaffType !== 'all' && emp.staff_type !== filterStaffType) {
      return false;
    }
    // Then filter by search term
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      emp.name?.toLowerCase().includes(term) ||
      emp.employee_code?.toLowerCase().includes(term) ||
      emp.email?.toLowerCase().includes(term) ||
      emp.phone?.includes(term) ||
      emp.designation?.toLowerCase().includes(term)
    );
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Clean form data - convert empty strings to null for optional fields
      const cleanedData = { ...formData };
      
      // Fields that should be null if empty (optional enum/string fields)
      const optionalFields = [
        'phone', 'date_of_birth', 'gender', 'current_address', 'permanent_address',
        'date_of_joining', 'department', 'designation', 'reporting_manager_id',
        'aadhar_number', 'pan_number', 'user_password'
      ];
      
      optionalFields.forEach(field => {
        if (cleanedData[field] === '') {
          cleanedData[field] = null;
        }
      });
      
      // Clean nested objects - set to null if all values are empty
      if (cleanedData.salary_info && 
          cleanedData.salary_info.basic === 0 && 
          cleanedData.salary_info.hra === 0 &&
          cleanedData.salary_info.allowances === 0 &&
          cleanedData.salary_info.deductions === 0) {
        cleanedData.salary_info = null;
      }
      
      if (cleanedData.bank_details &&
          !cleanedData.bank_details.account_number &&
          !cleanedData.bank_details.ifsc_code &&
          !cleanedData.bank_details.bank_name &&
          !cleanedData.bank_details.branch) {
        cleanedData.bank_details = null;
      }
      
      if (cleanedData.emergency_contact &&
          !cleanedData.emergency_contact.name &&
          !cleanedData.emergency_contact.phone &&
          !cleanedData.emergency_contact.relation) {
        cleanedData.emergency_contact = null;
      }
      
      // Don't send working_hours for Shopfloor Staff
      if (cleanedData.staff_type === 'Shopfloor Staff') {
        cleanedData.working_hours = null;
      }
      
      if (editEmployee) {
        await axios.patch(`${API_URL}/hr-employees/${editEmployee.id}`, cleanedData, { headers: getAuthHeader() });
        toast.success('Employee updated successfully');
      } else {
        await axios.post(`${API_URL}/hr-employees`, cleanedData, { headers: getAuthHeader() });
        toast.success('Employee created successfully');
      }
      setOpen(false);
      setEditEmployee(null);
      setFormData(initialFormData);
      setActiveTab('basic');
      fetchEmployees();
    } catch (error) {
      // Handle validation errors from backend
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save employee';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors come as array
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      
      toast.error(errorMessage);
    }
  };

  const handleEdit = (emp) => {
    setEditEmployee(emp);
    setFormData({
      ...initialFormData,
      ...emp,
      staff_type: emp.staff_type || 'Office Staff',
      working_hours: emp.working_hours || initialFormData.working_hours,
      salary_info: emp.salary_info || initialFormData.salary_info,
      bank_details: emp.bank_details || initialFormData.bank_details,
      emergency_contact: emp.emergency_contact || initialFormData.emergency_contact
    });
    setActiveTab('basic');
    setOpen(true);
  };

  const handleDelete = async (empId) => {
    if (!confirm('Are you sure you want to mark this employee as resigned?')) return;
    try {
      await axios.delete(`${API_URL}/hr-employees/${empId}`, { headers: getAuthHeader() });
      toast.success('Employee marked as resigned');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to update employee');
    }
  };

  // Open Create System User dialog
  const openCreateUserDialog = (emp) => {
    setSelectedEmpForUser(emp);
    setUserPassword('');
    setUserRole('employee');
    setRestrictedAccess(false);
    setSelectedModules(['my_tasks']);
    setCreateUserOpen(true);
  };

  // Toggle module selection
  const toggleModule = (moduleValue) => {
    setSelectedModules(prev => 
      prev.includes(moduleValue) 
        ? prev.filter(m => m !== moduleValue)
        : [...prev, moduleValue]
    );
  };

  // State for showing generated password
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  // Handle Create System User
  const handleCreateSystemUser = async () => {
    if (!selectedEmpForUser) return;
    
    if (restrictedAccess && selectedModules.length === 0) {
      toast.error('Please select at least one module for restricted access');
      return;
    }
    
    setCreatingUser(true);
    try {
      // Find designation to get its details
      const empDesig = designations.find(d => d.name === selectedEmpForUser.designation);
      
      const requestData = { 
        // No password - will be auto-generated
        designation_id: empDesig?.id || null,
        designation_name: selectedEmpForUser.designation || ''
      };
      
      // If restricted access is enabled, pass the selected modules
      if (restrictedAccess) {
        requestData.allowed_modules = selectedModules;
      }
      
      const response = await axios.post(
        `${API_URL}/hr-employees/${selectedEmpForUser.id}/create-user`,
        requestData,
        { headers: getAuthHeader() }
      );
      
      // Show the generated password to admin
      if (response.data.temporary_password) {
        setGeneratedPassword({
          name: selectedEmpForUser.name,
          email: response.data.email,
          password: response.data.temporary_password
        });
        setShowPasswordDialog(true);
      }
      
      const modulesMsg = restrictedAccess 
        ? ` with restricted access (${selectedModules.join(', ')})` 
        : '';
      toast.success(`System account created for ${selectedEmpForUser.name}${modulesMsg}`);
      setCreateUserOpen(false);
      setSelectedEmpForUser(null);
      setUserPassword('');
      setRestrictedAccess(false);
      setSelectedModules(['my_tasks']);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create system user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type on frontend
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExt)) {
      toast.error(`Unsupported file format: ${file.name}. Please upload a .csv or .xlsx file`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/hr-employees/bulk-upload`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.created > 0) {
        toast.success(`Successfully created ${response.data.created} employees`);
      }
      
      if (response.data.errors?.length > 0) {
        // Show first 5 errors in toast
        const firstErrors = response.data.errors.slice(0, 5);
        const errorMsg = firstErrors.join('\n');
        toast.error(`${response.data.errors.length} rows had errors:\n${errorMsg}${response.data.errors.length > 5 ? '\n... and more' : ''}`);
        console.log('All upload errors:', response.data.errors);
        
        // Show alert with all errors for easier viewing
        alert(`Upload Errors (${response.data.errors.length} total):\n\n${response.data.errors.slice(0, 20).join('\n')}${response.data.errors.length > 20 ? '\n\n... Check browser console for all errors' : ''}`);
      }
      
      fetchEmployees();
    } catch (error) {
      console.error('Bulk upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload file. Please check file format and try again.';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleBulkUpdateStaffType = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/hr-employees/bulk-update-staff-type`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Successfully updated staff type for ${response.data.updated} employees`);
      if (response.data.errors?.length > 0) {
        console.log('Update errors:', response.data.errors);
      }
      fetchEmployees();
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update staff types');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    // Comprehensive employee upload template with all fields
    const headers = [
      // Basic Info
      'NAME', 'EMAIL', 'PHONE', 'DATE_OF_BIRTH', 'GENDER', 'CURRENT_ADDRESS', 'PERMANENT_ADDRESS', 'AADHAR_NUMBER', 'PAN_NUMBER',
      // Employment
      'DATE_OF_JOINING', 'DEPARTMENT', 'DESIGNATION', 'EMPLOYMENT_TYPE', 'STATUS', 'STAFF_TYPE', 'SHIFT_START', 'SHIFT_END',
      // Salary
      'SALARY_BASIC', 'SALARY_HRA', 'SALARY_ALLOWANCES', 'SALARY_DEDUCTIONS',
      // Bank Details
      'BANK_ACCOUNT_NUMBER', 'BANK_IFSC', 'BANK_NAME', 'BANK_BRANCH',
      // Emergency Contact
      'EMERGENCY_CONTACT_NAME', 'EMERGENCY_CONTACT_PHONE', 'EMERGENCY_CONTACT_RELATION'
    ];
    
    // Sample data row
    const sampleRow = [
      'JOHN DOE', 'john@example.com', '9876543210', '1990-01-15', 'Male', '123 MAIN STREET MUMBAI', '456 HOME TOWN', '123456789012', 'ABCDE1234F',
      '2024-01-01', 'Production', 'MANAGER', 'Full-time', 'Active', 'Office Staff', '09:00', '18:00',
      '25000', '10000', '5000', '2000',
      '1234567890', 'SBIN0001234', 'STATE BANK OF INDIA', 'MUMBAI MAIN',
      'JANE DOE', '9876543211', 'SPOUSE'
    ];
    
    // Instructions row
    const instructions = [
      '(Required)', '(Optional)', '(Optional)', '(YYYY-MM-DD)', '(Male/Female/Other)', '(Optional)', '(Optional)', '(12 digits)', '(10 chars)',
      '(YYYY-MM-DD)', '(Production/Purchase/Sales/Marketing/HR & Admin/MDO/Inventory)', '(Optional)', '(Full-time/Part-time/Contract/Intern)', '(Active/Inactive/Resigned/Terminated)', '(Office Staff/Shopfloor Staff)', '(HH:MM for Office Staff)', '(HH:MM for Office Staff)',
      '(Number)', '(Number)', '(Number)', '(Number)',
      '(Optional)', '(Optional)', '(Optional)', '(Optional)',
      '(Optional)', '(Optional)', '(Optional)'
    ];
    
    const csvContent = headers.join(',') + '\n' + instructions.join(',') + '\n' + sampleRow.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'employee_upload_template.csv';
    link.click();
  };

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const calculateGrossSalary = () => {
    const { basic, hra, allowances } = formData.salary_info;
    return (parseFloat(basic) || 0) + (parseFloat(hra) || 0) + (parseFloat(allowances) || 0);
  };

  const calculateNetSalary = () => {
    const gross = calculateGrossSalary();
    const deductions = parseFloat(formData.salary_info.deductions) || 0;
    return gross - deductions;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6" />
              Employee Master
            </h1>
            <p className="text-muted-foreground">Manage employee records and HR data</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Template
            </Button>
            <label>
              <Button variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Bulk Upload'}
                </span>
              </Button>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkUpload} className="hidden" />
            </label>
            <label>
              <Button variant="outline" disabled={uploading} className="bg-orange-50 border-orange-200 hover:bg-orange-100" asChild>
                <span>
                  <Upload className="w-4 h-4 mr-2 text-orange-600" />
                  Update Staff Types
                </span>
              </Button>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkUpdateStaffType} className="hidden" />
            </label>
            <Dialog open={open} onOpenChange={(isOpen) => {
              setOpen(isOpen);
              if (!isOpen) {
                setEditEmployee(null);
                setFormData(initialFormData);
                setActiveTab('basic');
              }
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
                  <DialogDescription>
                    {editEmployee ? 'Update employee information' : 'Enter employee details across all tabs'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate}>
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-5">
                      <TabsTrigger value="basic">Basic Info</TabsTrigger>
                      <TabsTrigger value="employment">Employment</TabsTrigger>
                      <TabsTrigger value="salary">Salary</TabsTrigger>
                      <TabsTrigger value="bank">Bank Details</TabsTrigger>
                      <TabsTrigger value="emergency">Emergency</TabsTrigger>
                    </TabsList>

                    {/* Basic Info Tab */}
                    <TabsContent value="basic" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Full Name *</Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => updateFormField('name', e.target.value.toUpperCase())}
                            placeholder="FULL NAME"
                            required
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => updateFormField('email', e.target.value.toLowerCase())}
                            placeholder="email@example.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={formData.phone}
                            onChange={(e) => updateFormField('phone', e.target.value)}
                            placeholder="9876543210"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date of Birth</Label>
                          <Input
                            type="date"
                            value={formData.date_of_birth}
                            onChange={(e) => updateFormField('date_of_birth', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Gender</Label>
                          <Select value={formData.gender} onValueChange={(v) => updateFormField('gender', v)}>
                            <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                            <SelectContent>
                              {GENDERS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Aadhar Number</Label>
                          <Input
                            value={formData.aadhar_number}
                            onChange={(e) => updateFormField('aadhar_number', e.target.value)}
                            placeholder="1234 5678 9012"
                            maxLength={14}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>PAN Number</Label>
                          <Input
                            value={formData.pan_number}
                            onChange={(e) => updateFormField('pan_number', e.target.value.toUpperCase())}
                            placeholder="ABCDE1234F"
                            maxLength={10}
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Current Address</Label>
                        <Textarea
                          value={formData.current_address}
                          onChange={(e) => updateFormField('current_address', e.target.value.toUpperCase())}
                          placeholder="CURRENT ADDRESS"
                          rows={2}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Permanent Address</Label>
                        <Textarea
                          value={formData.permanent_address}
                          onChange={(e) => updateFormField('permanent_address', e.target.value.toUpperCase())}
                          placeholder="PERMANENT ADDRESS (IF DIFFERENT)"
                          rows={2}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>
                    </TabsContent>

                    {/* Employment Tab */}
                    <TabsContent value="employment" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date of Joining</Label>
                          <Input
                            type="date"
                            value={formData.date_of_joining}
                            onChange={(e) => updateFormField('date_of_joining', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Department</Label>
                          <Select value={formData.department} onValueChange={(v) => updateFormField('department', v)}>
                            <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                            <SelectContent>
                              {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Designation *</Label>
                          <Select 
                            value={formData.designation_id || ''} 
                            onValueChange={handleDesignationChange}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select designation" />
                            </SelectTrigger>
                            <SelectContent>
                              {designations.map(d => (
                                <SelectItem key={d.id} value={d.id}>
                                  {d.name} {d.department && `(${d.department})`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Permissions are based on designation. Manage in Designation Master.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Employment Type</Label>
                          <Select value={formData.employment_type} onValueChange={(v) => updateFormField('employment_type', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {EMPLOYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Staff Type *</Label>
                          <Select value={formData.staff_type} onValueChange={(v) => updateFormField('staff_type', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STAFF_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Reporting Manager</Label>
                          <Select value={formData.reporting_manager_id || 'none'} onValueChange={(v) => updateFormField('reporting_manager_id', v === 'none' ? '' : v)}>
                            <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- None --</SelectItem>
                              {employees.filter(e => e.id !== editEmployee?.id).map(e => (
                                <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select value={formData.status} onValueChange={(v) => updateFormField('status', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Inactive">Inactive</SelectItem>
                              <SelectItem value="Resigned">Resigned</SelectItem>
                              <SelectItem value="Terminated">Terminated</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Working Hours Section - Only for Office Staff */}
                      {formData.staff_type === 'Office Staff' && (
                        <Card className="mt-4">
                          <CardHeader className="py-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Working Hours (Mon-Sat)
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Shift Start</Label>
                                <Input
                                  type="time"
                                  value={formData.working_hours?.shift_start || '09:00'}
                                  onChange={(e) => updateNestedField('working_hours', 'shift_start', e.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Shift End</Label>
                                <Input
                                  type="time"
                                  value={formData.working_hours?.shift_end || '18:00'}
                                  onChange={(e) => updateNestedField('working_hours', 'shift_end', e.target.value)}
                                />
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Working hours are used for TAT calculation. Sundays are automatically non-working.
                            </p>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Create User Account Section */}
                      {!editEmployee && (
                        <Card className="mt-4">
                          <CardHeader className="py-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <User className="w-4 h-4" />
                              System Access
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={formData.create_user_account}
                                onCheckedChange={(checked) => updateFormField('create_user_account', checked)}
                              />
                              <Label>Create system login for this employee</Label>
                            </div>
                            {formData.create_user_account && (
                              <div className="grid grid-cols-2 gap-4 pl-6">
                                <div className="space-y-2">
                                  <Label>Password *</Label>
                                  <Input
                                    type="password"
                                    value={formData.user_password}
                                    onChange={(e) => updateFormField('user_password', e.target.value)}
                                    placeholder="Min 6 characters"
                                    required={formData.create_user_account}
                                    minLength={6}
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    User permissions will be based on designation. Manage in Designation Master.
                                  </p>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* Salary Tab */}
                    <TabsContent value="salary" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Basic Salary (₹)</Label>
                          <Input
                            type="number"
                            value={formData.salary_info.basic}
                            onChange={(e) => updateNestedField('salary_info', 'basic', e.target.value)}
                            min="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>HRA (₹)</Label>
                          <Input
                            type="number"
                            value={formData.salary_info.hra}
                            onChange={(e) => updateNestedField('salary_info', 'hra', e.target.value)}
                            min="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Allowances (₹)</Label>
                          <Input
                            type="number"
                            value={formData.salary_info.allowances}
                            onChange={(e) => updateNestedField('salary_info', 'allowances', e.target.value)}
                            min="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Deductions (₹)</Label>
                          <Input
                            type="number"
                            value={formData.salary_info.deductions}
                            onChange={(e) => updateNestedField('salary_info', 'deductions', e.target.value)}
                            min="0"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                        <div className="p-4 bg-muted rounded-lg">
                          <p className="text-sm text-muted-foreground">Gross Salary</p>
                          <p className="text-2xl font-bold">₹{calculateGrossSalary().toLocaleString()}</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Net Salary</p>
                          <p className="text-2xl font-bold text-green-600">₹{calculateNetSalary().toLocaleString()}</p>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Bank Details Tab */}
                    <TabsContent value="bank" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Account Number</Label>
                          <Input
                            value={formData.bank_details.account_number}
                            onChange={(e) => updateNestedField('bank_details', 'account_number', e.target.value)}
                            placeholder="Account number"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>IFSC Code</Label>
                          <Input
                            value={formData.bank_details.ifsc_code}
                            onChange={(e) => updateNestedField('bank_details', 'ifsc_code', e.target.value.toUpperCase())}
                            placeholder="IFSC CODE"
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Bank Name</Label>
                          <Input
                            value={formData.bank_details.bank_name}
                            onChange={(e) => updateNestedField('bank_details', 'bank_name', e.target.value.toUpperCase())}
                            placeholder="BANK NAME"
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Branch</Label>
                          <Input
                            value={formData.bank_details.branch}
                            onChange={(e) => updateNestedField('bank_details', 'branch', e.target.value.toUpperCase())}
                            placeholder="BRANCH NAME"
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>
                      </div>
                    </TabsContent>

                    {/* Emergency Contact Tab */}
                    <TabsContent value="emergency" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Contact Name</Label>
                          <Input
                            value={formData.emergency_contact.name}
                            onChange={(e) => updateNestedField('emergency_contact', 'name', e.target.value.toUpperCase())}
                            placeholder="CONTACT NAME"
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Contact Phone</Label>
                          <Input
                            value={formData.emergency_contact.phone}
                            onChange={(e) => updateNestedField('emergency_contact', 'phone', e.target.value)}
                            placeholder="9876543210"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Relation</Label>
                          <Input
                            value={formData.emergency_contact.relation}
                            onChange={(e) => updateNestedField('emergency_contact', 'relation', e.target.value.toUpperCase())}
                            placeholder="SPOUSE, PARENT, SIBLING..."
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                  <DialogFooter className="mt-6">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button type="submit">{editEmployee ? 'Update Employee' : 'Create Employee'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, code, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterDepartment} onValueChange={setFilterDepartment}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
              <SelectItem value="Resigned">Resigned</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStaffType} onValueChange={setFilterStaffType}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Staff Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff Types</SelectItem>
              <SelectItem value="Office Staff">Office Staff</SelectItem>
              <SelectItem value="Shopfloor Staff">Shopfloor Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Employees</p>
              <p className="text-2xl font-bold">{employees.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold text-green-600">{employees.filter(e => e.status === 'Active').length}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700">Office Staff</p>
              <p className="text-2xl font-bold text-blue-600">{employees.filter(e => e.staff_type === 'Office Staff').length}</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50">
            <CardContent className="p-4">
              <p className="text-sm text-orange-700">Shopfloor Staff</p>
              <p className="text-2xl font-bold text-orange-600">{employees.filter(e => e.staff_type === 'Shopfloor Staff').length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">With System Access</p>
              <p className="text-2xl font-bold text-purple-600">{employees.filter(e => e.user_id).length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Staff Type</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No employees found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((emp) => (
                  <TableRow key={emp.id}>
                    <TableCell className="font-mono text-sm">{emp.employee_code}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium">{emp.name?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium">{emp.name}</p>
                          {emp.user_id && <Badge variant="outline" className="text-xs">Has Login</Badge>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {emp.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{emp.phone}</div>}
                        {emp.email && <div className="flex items-center gap-1 text-muted-foreground"><Mail className="w-3 h-3" />{emp.email}</div>}
                      </div>
                    </TableCell>
                    <TableCell>{emp.department || '-'}</TableCell>
                    <TableCell>
                      <Badge className={staffTypeColors[emp.staff_type] || 'bg-gray-100'}>
                        {emp.staff_type || 'Office Staff'}
                      </Badge>
                    </TableCell>
                    <TableCell>{emp.designation || '-'}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[emp.status] || 'bg-gray-100'}>
                        {emp.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Create System User Button - only show if no user_id and has email */}
                        {!emp.user_id && emp.email && emp.status === 'Active' && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => openCreateUserDialog(emp)}
                            title="Create System Login"
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(emp)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(emp.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create System User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Create System Login
            </DialogTitle>
            <DialogDescription>
              Create a system login account for <strong>{selectedEmpForUser?.name}</strong> ({selectedEmpForUser?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Auto-generated password notice */}
            <div className="p-3 border rounded-lg bg-blue-50 border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Password will be auto-generated.</strong> The user will be required to change it on first login.
              </p>
            </div>
            
            {/* Restricted Access Toggle */}
            <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox
                  id="restrictedAccess"
                  checked={restrictedAccess}
                  onCheckedChange={(checked) => setRestrictedAccess(checked)}
                />
                <Label htmlFor="restrictedAccess" className="font-medium text-amber-800">
                  Restricted Access (Preview Testing)
                </Label>
              </div>
              <p className="text-xs text-amber-700 ml-6">
                Enable this to limit user to specific modules only. Use for Preview mode testing.
              </p>
              
              {/* Module Selection - Only show if restricted access is enabled */}
              {restrictedAccess && (
                <div className="mt-3 ml-6 space-y-2">
                  <Label className="text-sm text-amber-800">Select Allowed Modules:</Label>
                  <div className="space-y-2">
                    {AVAILABLE_MODULES.map((module) => (
                      <div key={module.value} className="flex items-start gap-2">
                        <Checkbox
                          id={`module-${module.value}`}
                          checked={selectedModules.includes(module.value)}
                          onCheckedChange={() => toggleModule(module.value)}
                        />
                        <div>
                          <Label htmlFor={`module-${module.value}`} className="text-sm font-medium">
                            {module.label}
                          </Label>
                          <p className="text-xs text-muted-foreground">{module.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {!restrictedAccess && (
              <p className="text-xs text-muted-foreground">
                User permissions will be based on the employee&apos;s designation ({selectedEmpForUser?.designation || 'not set'}). 
                You can manage permissions in Designation Master.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSystemUser} disabled={creatingUser}>
              {creatingUser ? 'Creating...' : 'Create Login'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generated Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Shield className="w-5 h-5" />
              User Account Created!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 border rounded-lg bg-green-50 border-green-200">
              <p className="text-sm text-green-800 mb-3">
                Share these credentials with <strong>{generatedPassword?.name}</strong>:
              </p>
              <div className="space-y-2 font-mono text-sm">
                <div className="flex justify-between items-center bg-white p-2 rounded">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-semibold">{generatedPassword?.email}</span>
                </div>
                <div className="flex justify-between items-center bg-white p-2 rounded">
                  <span className="text-gray-600">Password:</span>
                  <span className="font-semibold text-lg tracking-wider">{generatedPassword?.password}</span>
                </div>
              </div>
            </div>
            
            <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> The user will be required to change this password on first login.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => {
              // Copy credentials to clipboard
              const text = `Email: ${generatedPassword?.email}\nPassword: ${generatedPassword?.password}`;
              navigator.clipboard.writeText(text);
              toast.success('Credentials copied to clipboard!');
            }} variant="outline">
              Copy Credentials
            </Button>
            <Button onClick={() => setShowPasswordDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
